import React, { useState, useEffect, useRef } from 'react';
import { 
    Radio, Play, Square, Users, Eye, ShoppingBag, MessageSquare, 
    Pin, AlertCircle, Sparkles, CheckCircle, Video, Mic, MicOff, 
    VideoOff, ExternalLink, Settings, TrendingUp, Heart, Send, 
    Shield, X, Globe, Layers, Clock, Award, DollarSign, VolumeX,
    UserX, Trash2, ShieldAlert, PinOff, Plus, Check
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getProducts, liveAPI } from '../api/client';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';

export default function SellerLive() {
    const { user } = useAuth();
    const rtl = isRTL();

    // Broadcast State
    const [isLive, setIsLive] = useState(false);
    const [liveSession, setLiveSession] = useState(null);
    const [showGoLiveModal, setShowGoLiveModal] = useState(false);
    const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
    const [postLiveMetrics, setPostLiveMetrics] = useState(null);

    // Setup Form State
    const [streamTitle, setStreamTitle] = useState('');
    const [streamDescription, setStreamDescription] = useState('');
    const [category, setCategory] = useState('Watches & Jewelry');
    const [targetMarket, setTargetMarket] = useState('SA');
    const [audience, setAudience] = useState('RETAIL'); // RETAIL, WHOLESALE, BOTH
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [pinnedProductId, setPinnedProductId] = useState(null);

    // Hardware Preview State
    const [micEnabled, setMicEnabled] = useState(true);
    const [camEnabled, setCamEnabled] = useState(true);
    const videoPreviewRef = useRef(null);
    const streamRef = useRef(null);

    // Real-time live session simulation / data
    const [viewerCount, setViewerCount] = useState(0);
    const [peakViewers, setPeakViewers] = useState(0);
    const [likesCount, setLikesCount] = useState(0);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [pinnedMessage, setPinnedMessage] = useState(null);

    // Live Moderation State
    const [mutedUsers, setMutedUsers] = useState({}); // userId/name -> { duration, expiresAt, reason }
    const [blockedUsers, setBlockedUsers] = useState([]); // user names
    const [selectedModerationUser, setSelectedModerationUser] = useState(null);
    const [showMuteModal, setShowMuteModal] = useState(false);

    // Products Catalog
    const [catalogProducts, setCatalogProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);

    const markets = [
        { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
        { code: 'YE', name: 'Yemen', flag: '🇾🇪' },
        { code: 'SO', name: 'Somalia', flag: '🇸🇴' },
        { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪' },
        { code: 'OM', name: 'Oman', flag: '🇴🇲' },
        { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
        { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
        { code: 'US', name: 'United States', flag: '🇺🇸' }
    ];

    const categories = [
        'Watches & Jewelry',
        'Luxury Fashion & Couture',
        'Fine Fragrance & Oud',
        'Rare Gemstones & Gold',
        'Electronics & High Tech',
        'Raw Materials & Bulk Food',
        'Leather Goods & Accessories'
    ];

    // Load Seller Products
    useEffect(() => {
        let mounted = true;
        getProducts()
            .then(res => {
                if (!mounted) return;
                const list = res?.data || res || [];
                setCatalogProducts(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                if (mounted) {
                    setCatalogProducts([
                        { id: 101, name: 'Royal Chronograph Sapphire 42mm', price: 4200, currency: 'SAR', imageUrl: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=600&auto=format&fit=crop&q=80' },
                        { id: 102, name: 'Heritage Emerald Ring 18K', price: 8900, currency: 'SAR', imageUrl: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=600&auto=format&fit=crop&q=80' },
                        { id: 103, name: 'Pure Cambodian Oud Extract 12ml', price: 1650, currency: 'SAR', imageUrl: 'https://images.unsplash.com/photo-1592945403244-b3fbafd7f539?w=600&auto=format&fit=crop&q=80' }
                    ]);
                }
            })
            .finally(() => {
                if (mounted) setLoadingProducts(false);
            });
        return () => { mounted = false; };
    }, []);

    // Timer and viewer simulation during Live
    useEffect(() => {
        let timer = null;
        let viewerInterval = null;
        let simulatedChatInterval = null;

        if (isLive) {
            timer = setInterval(() => {
                setElapsedSeconds(prev => prev + 1);
            }, 1000);

            // Viewer fluctuation
            viewerInterval = setInterval(() => {
                setViewerCount(prev => {
                    const delta = Math.floor(Math.random() * 7) - 2;
                    const next = Math.max(25, prev + delta);
                    setPeakViewers(currentPeak => Math.max(currentPeak, next));
                    return next;
                });
                setLikesCount(prev => prev + Math.floor(Math.random() * 4));
            }, 3000);

            // Viewer chat comments simulation
            const viewerNames = ['Fahad Al-Otaibi', 'Maryam Sultan', 'Rashid Al-Nuaimi', 'Dr. Tariq', 'Amina Somali', 'Sultan Luxury', 'Layla K.'];
            const sampleQuestions = [
                'ما شاء الله، هل الساعة مقاومة للماء 100 متر؟',
                'Is international express shipping included to Dubai?',
                'هل يتوفر سعر خاص لطلبيات الجملة؟',
                'قطعة فاخرة جداً، هل يوجد شهادة ضمان دولي؟',
                'أنا أتابع من الرياض، كم يستغرق التوصيل؟',
                'Can you show the back of the case please?',
                'تمت إضافة المنتج للسلة الآن!'
            ];

            simulatedChatInterval = setInterval(() => {
                const randomUser = viewerNames[Math.floor(Math.random() * viewerNames.length)];
                
                // Check if user is blocked or muted
                if (blockedUsers.includes(randomUser)) return;
                const muteInfo = mutedUsers[randomUser];
                if (muteInfo && muteInfo.expiresAt > Date.now()) return;

                const randomText = sampleQuestions[Math.floor(Math.random() * sampleQuestions.length)];
                setChatMessages(prev => [
                    ...prev.slice(-40),
                    {
                        id: Date.now() + Math.random(),
                        sender: randomUser,
                        text: randomText,
                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        isHost: false
                    }
                ]);
            }, 8000);
        }

        return () => {
            if (timer) clearInterval(timer);
            if (viewerInterval) clearInterval(viewerInterval);
            if (simulatedChatInterval) clearInterval(simulatedChatInterval);
        };
    }, [isLive, mutedUsers, blockedUsers]);

    // Camera acquisition
    const startCamera = async () => {
        try {
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                streamRef.current = stream;
                if (videoPreviewRef.current) {
                    videoPreviewRef.current.srcObject = stream;
                }
            }
        } catch (err) {
            console.warn('Camera preview not accessible, fallback to interactive test pattern:', err);
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        if (videoPreviewRef.current) {
            videoPreviewRef.current.srcObject = null;
        }
    };

    const toggleMic = () => {
        if (streamRef.current) {
            streamRef.current.getAudioTracks().forEach(t => {
                t.enabled = !micEnabled;
            });
        }
        setMicEnabled(!micEnabled);
    };

    const toggleCam = () => {
        if (streamRef.current) {
            streamRef.current.getVideoTracks().forEach(t => {
                t.enabled = !camEnabled;
            });
        }
        setCamEnabled(!camEnabled);
    };

    const handleOpenGoLiveModal = () => {
        setStreamTitle(`Exclusive Showcase: ${user?.name || 'Maison'} Fine Selection`);
        setStreamDescription('Live demonstration of newly curated luxury timepieces and rare items.');
        if (catalogProducts.length > 0 && selectedProductIds.length === 0) {
            setSelectedProductIds([catalogProducts[0].id]);
            setPinnedProductId(catalogProducts[0].id);
        }
        setShowGoLiveModal(true);
        startCamera();
    };

    const handleStartLive = async () => {
        setShowGoLiveModal(false);
        let realRoomId = 'live_' + Date.now();
        let backendRoom = null;

        try {
            if (liveAPI?.createRoom) {
                backendRoom = await liveAPI.createRoom({
                    title: streamTitle || 'بث مباشر فاخر من متجر LUXVONO',
                    description: streamDescription,
                    category,
                    type: 'LIVE_COMMERCE',
                    availableCountries: [targetMarket || 'ALL'],
                    productIds: selectedProductIds
                });
                if (backendRoom && backendRoom.id) {
                    realRoomId = backendRoom.id;
                }
            }
        } catch (e) {
            console.error('Error creating live room on backend:', e);
        }

        const sessionPayload = {
            id: realRoomId,
            backendRoomId: backendRoom?.id || realRoomId,
            title: streamTitle,
            description: streamDescription,
            category,
            targetMarket,
            audience,
            streamKey: backendRoom?.streamKey || ('live_sk_' + Math.random().toString(36).substring(2, 10)),
            streamUrl: backendRoom?.streamKey 
                ? `https://luxvono.com/media/hls/${backendRoom.streamKey}.m3u8`
                : `https://luxvono.com/media/hls/${realRoomId}.m3u8`,
            startTime: new Date().toISOString()
        };

        setLiveSession(sessionPayload);
        setIsLive(true);
        setViewerCount(68);
        setPeakViewers(68);
        setLikesCount(180);
        setElapsedSeconds(0);
        setChatMessages([
            {
                id: 1,
                sender: 'LUXVONO System',
                text: '★ Live broadcast initialized. Connected to Sovereign HLS Streaming Ingest.',
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                isSystem: true
            }
        ]);
    };

    const handleEndLive = async () => {
        stopCamera();
        setIsLive(false);

        // Immediately close the room on the backend so all clients/customers get ROOM_ENDED / ROOM_CLOSED in the exact same instant!
        const targetId = liveSession?.backendRoomId || liveSession?.id;
        if (targetId) {
            try {
                await liveAPI.closeRoom(targetId);
            } catch (err) {
                console.error('Error closing live room on backend:', err);
            }
        }

        // Calculate final post-stream analytics
        const finalMetrics = {
            durationSeconds: elapsedSeconds,
            peakViewers: Math.max(peakViewers, viewerCount),
            totalUniqueViewers: Math.floor(peakViewers * 2.8) + 45,
            productClicks: Math.floor(peakViewers * 1.4) + 18,
            addToBags: Math.floor(peakViewers * 0.35) + 6,
            ordersReceived: Math.floor(peakViewers * 0.12) + 2,
            followersGained: Math.floor(peakViewers * 0.45) + 9,
            chatMessagesCount: chatMessages.length + 24,
            likesTotal: likesCount + 120
        };

        setPostLiveMetrics(finalMetrics);
        setShowAnalyticsModal(true);
    };

    const handleSendChatMessage = (e) => {
        e.preventDefault();
        if (!chatInput.trim()) return;

        const newMsg = {
            id: Date.now(),
            sender: user?.name || (rtl ? 'المضيف (أنت)' : 'Host (You)'),
            text: chatInput.trim(),
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            isHost: true
        };

        setChatMessages(prev => [...prev, newMsg]);
        setChatInput('');
    };

    // Live Moderation Actions
    const handleMuteUser = (durationMinutes) => {
        if (!selectedModerationUser) return;
        const expiresAt = durationMinutes === -1 ? Date.now() + 86400000 : Date.now() + durationMinutes * 60 * 1000;
        setMutedUsers(prev => ({
            ...prev,
            [selectedModerationUser]: {
                duration: durationMinutes === -1 ? 'UNTIL_END' : `${durationMinutes}m`,
                expiresAt
            }
        }));

        setChatMessages(prev => [
            ...prev,
            {
                id: Date.now(),
                sender: 'Moderation',
                text: `🔇 ${selectedModerationUser} was muted by host (${durationMinutes === -1 ? 'until stream ends' : durationMinutes + ' mins'}).`,
                isSystem: true
            }
        ]);

        setShowMuteModal(false);
        setSelectedModerationUser(null);
    };

    const handleBlockUser = (userName) => {
        setBlockedUsers(prev => [...prev, userName]);
        setChatMessages(prev => [
            ...prev,
            {
                id: Date.now(),
                sender: 'Moderation',
                text: `🚫 ${userName} was blocked by host and removed from live stream.`,
                isSystem: true
            }
        ]);
        // Also persist in local storage for seller blocking rules
        try {
            const raw = localStorage.getItem('luxvono_seller_blocked_users');
            const list = raw ? JSON.parse(raw) : [];
            if (!list.includes(userName)) list.push(userName);
            localStorage.setItem('luxvono_seller_blocked_users', JSON.stringify(list));
        } catch (_) {}
    };

    const handleDeleteMessage = (msgId) => {
        setChatMessages(prev => prev.filter(m => m.id !== msgId));
    };

    const handlePinMessage = (msg) => {
        setPinnedMessage(pinnedMessage?.id === msg.id ? null : msg);
    };

    const formatDuration = (totalSec) => {
        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const s = totalSec % 60;
        if (h > 0) {
            return `${h}:${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
        }
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const toggleSelectProduct = (id) => {
        if (selectedProductIds.includes(id)) {
            setSelectedProductIds(prev => prev.filter(x => x !== id));
            if (pinnedProductId === id) setPinnedProductId(null);
        } else {
            setSelectedProductIds(prev => [...prev, id]);
            if (!pinnedProductId) setPinnedProductId(id);
        }
    };

    const pinnedProduct = catalogProducts.find(p => p.id === pinnedProductId) || 
                          (selectedProductIds.length > 0 ? catalogProducts.find(p => p.id === selectedProductIds[0]) : null);

    return (
        <div style={{ color: '#f8fafc', paddingBottom: 60 }}>
            {/* Header Bar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: isLive ? '#ef4444' : '#10b981', boxShadow: isLive ? '0 0 12px #ef4444' : 'none' }}></span>
                        <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>
                            {rtl ? 'استوديو البث المباشر والتسوق الحي' : 'Live Broadcaster Studio & Live Commerce'}
                        </h1>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>
                        {rtl 
                            ? 'بث مباشر عالي الدقة موزع عبر شبكة HLS التفاعلية، مع ربط عدة منتجات، تثبيت العروض، وأدوات إدارة المحادثة.' 
                            : 'Broadcast high-fidelity HLS live streams, pin catalog pieces, manage multiple products, and moderate live chat.'}
                    </p>
                </div>

                {!isLive ? (
                    <button
                        onClick={handleOpenGoLiveModal}
                        style={{
                            background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                            color: '#fff',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: 10,
                            fontWeight: 700,
                            fontSize: 14,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            boxShadow: '0 4px 16px rgba(239,68,68,0.35)'
                        }}
                    >
                        <Radio size={18} />
                        <span>{rtl ? 'بدء بث مباشر الآن (GO LIVE)' : 'GO LIVE NOW'}</span>
                    </button>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid #ef4444', borderRadius: 8, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', animation: 'pulse 1.5s infinite' }}></span>
                            <span style={{ color: '#ef4444', fontWeight: 800, fontSize: 13 }}>LIVE</span>
                            <span style={{ color: '#f8fafc', fontWeight: 600, fontSize: 13 }}>{formatDuration(elapsedSeconds)}</span>
                        </div>
                        <button
                            onClick={handleEndLive}
                            style={{
                                background: '#334155',
                                color: '#fff',
                                border: '1px solid #475569',
                                padding: '10px 18px',
                                borderRadius: 8,
                                fontWeight: 700,
                                fontSize: 13,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                            }}
                        >
                            <Square size={16} color="#ef4444" fill="#ef4444" />
                            <span>{rtl ? 'إنهاء البث' : 'End Stream'}</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Main Stage Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: isLive ? '1fr 360px' : '1fr', gap: 24 }}>
                {/* Left/Main Column: Video & Controls */}
                <div>
                    <div style={{
                        position: 'relative',
                        width: '100%',
                        height: 520,
                        background: '#070a12',
                        borderRadius: 16,
                        overflow: 'hidden',
                        border: '1px solid #1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.6)'
                    }}>
                        {/* Video Element */}
                        <video
                            ref={videoPreviewRef}
                            autoPlay
                            playsInline
                            muted
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: camEnabled ? 'block' : 'none'
                            }}
                        />

                        {/* Fallback if camera off or not live */}
                        {(!camEnabled || (!isLive && !streamRef.current)) && (
                            <div style={{ textAlign: 'center', padding: 24, zIndex: 2 }}>
                                <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(254,44,85,0.1)', border: '1px solid rgba(254,44,85,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                    <Radio size={36} color="#FE2C55" />
                                </div>
                                <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px 0', color: '#f8fafc' }}>
                                    {isLive ? (rtl ? 'الكاميرا متوقفة مؤقتاً' : 'Camera Muted') : (rtl ? 'استوديو البث في وضع الاستعداد' : 'Studio In Standby Mode')}
                                </h3>
                                <p style={{ fontSize: 13, color: '#94a3b8', maxWidth: 420, margin: '0 auto 20px' }}>
                                    {rtl 
                                        ? 'انقر على "بدء بث مباشر" لتهيئة الأسواق، ربط المنتجات، وبدء استقبال المشاهدين الحيين.'
                                        : 'Configure your target market, link catalog products, and broadcast through low-latency media ingest.'}
                                </p>
                                {!isLive && (
                                    <button
                                        onClick={handleOpenGoLiveModal}
                                        style={{ background: '#FE2C55', color: '#ffffff', border: 'none', padding: '10px 24px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 4px 14px rgba(254,44,85,0.35)' }}
                                    >
                                        {rtl ? 'تجهيز البث المباشر' : 'Setup Broadcast'}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Top HUD Overlay (Active when Live) */}
                        {isLive && (
                            <div style={{ position: 'absolute', top: 16, left: 16, right: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 10, pointerEvents: 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', padding: '6px 14px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Eye size={15} color="#38bdf8" />
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{viewerCount}</span>
                                        <span style={{ fontSize: 11, color: '#94a3b8' }}>{rtl ? 'مشاهد' : 'viewers'}</span>
                                    </div>
                                    <div style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', padding: '6px 14px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Heart size={14} color="#f43f5e" fill="#f43f5e" />
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{likesCount}</span>
                                    </div>
                                </div>

                                <div style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', padding: '6px 14px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 12, color: '#FE2C55', fontWeight: 600 }}>{targetMarket}</span>
                                    <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#64748b' }}></span>
                                    <span style={{ fontSize: 11, color: '#cbd5e1' }}>{audience}</span>
                                </div>
                            </div>
                        )}

                        {/* Bottom Floating Pinned Product Card (Inside video stream) */}
                        {isLive && pinnedProduct && (
                            <div style={{
                                position: 'absolute',
                                bottom: 76,
                                left: rtl ? 'auto' : 16,
                                right: rtl ? 16 : 'auto',
                                background: 'rgba(15, 23, 42, 0.92)',
                                backdropFilter: 'blur(12px)',
                                border: '1px solid rgba(254, 44, 85, 0.5)',
                                borderRadius: 12,
                                padding: '10px 14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                maxWidth: 360,
                                zIndex: 10,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
                            }}>
                                <img
                                    src={pinnedProduct.imageUrl || pinnedProduct.image}
                                    alt={pinnedProduct.name}
                                    style={{ width: 50, height: 50, borderRadius: 8, objectFit: 'cover', background: '#0b0f19' }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                        <Pin size={12} color="#FE2C55" />
                                        <span style={{ fontSize: 10, color: '#FE2C55', fontWeight: 800, textTransform: 'uppercase' }}>
                                            {rtl ? 'معروض للبيع بالبث' : 'Featured in Stream'}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {pinnedProduct.name}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#FE2C55', fontWeight: 700 }}>
                                        {pinnedProduct.price?.toLocaleString()} {pinnedProduct.currency || 'SAR'}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setPinnedProductId(null)}
                                    title="Unpin"
                                    style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 4 }}
                                >
                                    <PinOff size={16} />
                                </button>
                            </div>
                        )}

                        {/* Bottom Hardware Controls Bar */}
                        <div style={{
                            position: 'absolute',
                            bottom: 16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(15, 23, 42, 0.85)',
                            backdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 30,
                            padding: '6px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            zIndex: 10
                        }}>
                            <button
                                onClick={toggleMic}
                                title={micEnabled ? 'Mute Mic' : 'Unmute Mic'}
                                style={{ background: micEnabled ? '#334155' : '#ef4444', border: 'none', color: '#fff', width: 38, height: 38, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            >
                                {micEnabled ? <Mic size={18} /> : <MicOff size={18} />}
                            </button>
                            <button
                                onClick={toggleCam}
                                title={camEnabled ? 'Stop Video' : 'Start Video'}
                                style={{ background: camEnabled ? '#334155' : '#ef4444', border: 'none', color: '#fff', width: 38, height: 38, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            >
                                {camEnabled ? <Video size={18} /> : <VideoOff size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Attached Multi-Product Showcase Drawer */}
                    <div style={{ marginTop: 24, background: '#111827', border: '1px solid #1e293b', borderRadius: 14, padding: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ShoppingBag size={18} color="#FE2C55" />
                                <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#f8fafc' }}>
                                    {rtl ? 'قائمة منتجات البث المباشر (Multi-Product Showcase)' : 'Live Shopping Products'}
                                </h3>
                                <span style={{ fontSize: 12, background: '#1e293b', color: '#94a3b8', padding: '2px 8px', borderRadius: 12 }}>
                                    {selectedProductIds.length} {rtl ? 'منتجات مرفقة' : 'attached'}
                                </span>
                            </div>
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>
                                {rtl ? 'انقر على "تثبيت" لتغيير القطعة المعروضة للمشاهدين' : 'Click "Pin" to switch featured item shown to viewers'}
                            </span>
                        </div>

                        {loadingProducts ? (
                            <div style={{ padding: 24, textAlign: 'center' }}><Spinner /></div>
                        ) : catalogProducts.length === 0 ? (
                            <p style={{ fontSize: 13, color: '#64748b', textAlign: 'center', padding: '20px 0' }}>
                                {rtl ? 'لا توجد منتجات منشورة حالياً في كتالوجك.' : 'No published products found in your catalog.'}
                            </p>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                                {catalogProducts.map(p => {
                                    const isSelected = selectedProductIds.includes(p.id);
                                    const isPinned = pinnedProductId === p.id;
                                    return (
                                        <div
                                            key={p.id}
                                            style={{
                                                background: isPinned ? 'rgba(254,44,85,0.12)' : '#0b0f19',
                                                border: `1.5px solid ${isPinned ? '#FE2C55' : isSelected ? '#FE2C55' : '#1e293b'}`,
                                                borderRadius: 10,
                                                padding: 12,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12
                                            }}
                                        >
                                            <img
                                                src={p.imageUrl || p.image}
                                                alt={p.name}
                                                style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover' }}
                                            />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {p.name}
                                                </div>
                                                <div style={{ fontSize: 12, color: '#FE2C55', fontWeight: 700 }}>
                                                    {p.price?.toLocaleString()} {p.currency || 'SAR'}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {isLive && isSelected && (
                                                    <button
                                                        onClick={() => setPinnedProductId(isPinned ? null : p.id)}
                                                        style={{
                                                            background: isPinned ? '#FE2C55' : '#1e293b',
                                                            color: isPinned ? '#ffffff' : '#cbd5e1',
                                                            border: 'none',
                                                            borderRadius: 6,
                                                            padding: '4px 8px',
                                                            fontSize: 11,
                                                            fontWeight: 700,
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 4
                                                        }}
                                                    >
                                                        <Pin size={10} />
                                                        <span>{isPinned ? (rtl ? 'مثبت' : 'Pinned') : (rtl ? 'تثبيت' : 'Pin')}</span>
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => toggleSelectProduct(p.id)}
                                                    style={{
                                                        background: isSelected ? 'rgba(254,44,85,0.15)' : 'transparent',
                                                        color: isSelected ? '#FE2C55' : '#64748b',
                                                        border: `1px solid ${isSelected ? '#FE2C55' : '#334155'}`,
                                                        borderRadius: 6,
                                                        padding: '4px 8px',
                                                        fontSize: 11,
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {isSelected ? (rtl ? 'مرفق' : 'Attached') : (rtl ? 'إرفاق' : 'Attach')}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column: Live Chat & Realtime Moderation (Shown when Live) */}
                {isLive && (
                    <div style={{
                        background: '#111827',
                        border: '1px solid #1e293b',
                        borderRadius: 16,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 680,
                        overflow: 'hidden'
                    }}>
                        {/* Chat Header */}
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <MessageSquare size={16} color="#38bdf8" />
                                <span style={{ fontSize: 14, fontWeight: 700, color: '#f8fafc' }}>
                                    {rtl ? 'محادثات وإدارة البث' : 'Live Chat & Moderation'}
                                </span>
                            </div>
                            <span style={{ fontSize: 11, background: 'rgba(56,189,248,0.1)', color: '#38bdf8', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                                {chatMessages.length}
                            </span>
                        </div>

                        {/* Pinned Message Box */}
                        {pinnedMessage && (
                            <div style={{ background: 'rgba(254,44,85,0.1)', borderBottom: '1px solid rgba(254,44,85,0.25)', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                    <Pin size={13} color="#FE2C55" style={{ flexShrink: 0 }} />
                                    <span style={{ fontSize: 12, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <strong>{pinnedMessage.sender}:</strong> {pinnedMessage.text}
                                    </span>
                                </div>
                                <button onClick={() => setPinnedMessage(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 2 }}>
                                    <X size={14} />
                                </button>
                            </div>
                        )}

                        {/* Chat Messages Stream with Host Moderation Actions */}
                        <div style={{ flex: 1, padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {chatMessages.map(msg => (
                                <div
                                    key={msg.id}
                                    style={{
                                        background: msg.isSystem ? 'rgba(56,189,248,0.08)' : msg.isHost ? 'rgba(254,44,85,0.1)' : '#0b0f19',
                                        border: `1px solid ${msg.isSystem ? 'rgba(56,189,248,0.2)' : msg.isHost ? 'rgba(254,44,85,0.3)' : '#1e293b'}`,
                                        borderRadius: 10,
                                        padding: '10px 12px',
                                        position: 'relative'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <strong style={{ fontSize: 12, color: msg.isHost ? '#FE2C55' : '#f8fafc' }}>
                                                {msg.sender}
                                            </strong>
                                            {msg.isHost && (
                                                <span style={{ fontSize: 9, background: '#FE2C55', color: '#ffffff', fontWeight: 800, padding: '1px 5px', borderRadius: 4 }}>
                                                    HOST
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ fontSize: 10, color: '#64748b' }}>{msg.time}</span>
                                            
                                            {/* Moderation Dropdown Controls */}
                                            {!msg.isSystem && !msg.isHost && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <button
                                                        onClick={() => handlePinMessage(msg)}
                                                        title="Pin Message"
                                                        style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 2 }}
                                                    >
                                                        <Pin size={11} />
                                                    </button>
                                                    <button
                                                        onClick={() => { setSelectedModerationUser(msg.sender); setShowMuteModal(true); }}
                                                        title="Mute User"
                                                        style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', padding: 2 }}
                                                    >
                                                        <VolumeX size={11} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleBlockUser(msg.sender)}
                                                        title="Block User"
                                                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 2 }}
                                                    >
                                                        <UserX size={11} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteMessage(msg.id)}
                                                        title="Delete Message"
                                                        style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 2 }}
                                                    >
                                                        <Trash2 size={11} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <p style={{ margin: 0, fontSize: 13, color: '#cbd5e1', lineHeight: 1.4 }}>
                                        {msg.text}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Chat Composer */}
                        <form onSubmit={handleSendChatMessage} style={{ padding: 12, borderTop: '1px solid #1e293b', background: '#0b0f19', display: 'flex', gap: 8 }}>
                            <input
                                type="text"
                                value={chatInput}
                                onChange={e => setChatInput(e.target.value)}
                                placeholder={rtl ? 'أجب على استفسارات المشاهدين كبائع...' : 'Reply to viewers as host...'}
                                style={{
                                    flex: 1,
                                    background: '#1e293b',
                                    border: '1px solid #334155',
                                    borderRadius: 8,
                                    padding: '8px 12px',
                                    color: '#fff',
                                    fontSize: 13,
                                    outline: 'none'
                                }}
                            />
                            <button
                                type="submit"
                                style={{
                                    background: '#2563eb',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 8,
                                    padding: '0 14px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Send size={15} />
                            </button>
                        </form>
                    </div>
                )}
            </div>

            {/* Modal: Setup GO LIVE Configuration */}
            {showGoLiveModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: '#111827', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', padding: 28, boxShadow: '0 20px 40px rgba(0,0,0,0.7)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Radio size={22} color="#ef4444" />
                                <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: '#f8fafc' }}>
                                    {rtl ? 'إعداد جلسة البث المباشر (GO LIVE)' : 'Configure Live Broadcast'}
                                </h2>
                            </div>
                            <button onClick={() => { setShowGoLiveModal(false); stopCamera(); }} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                                <X size={20} />
                            </button>
                        </div>

                        {/* Title & Desc */}
                        <div style={{ marginBottom: 18 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>
                                {rtl ? 'عنوان البث المباشر' : 'Broadcast Title'}
                            </label>
                            <input
                                type="text"
                                value={streamTitle}
                                onChange={e => setStreamTitle(e.target.value)}
                                placeholder="e.g. VIP Watches Showcase"
                                style={{ width: '100%', background: '#0b0f19', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14 }}
                            />
                        </div>

                        <div style={{ marginBottom: 18 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>
                                {rtl ? 'وصف البث والموضوع' : 'Description'}
                            </label>
                            <textarea
                                value={streamDescription}
                                onChange={e => setStreamDescription(e.target.value)}
                                rows={2}
                                style={{ width: '100%', background: '#0b0f19', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, resize: 'none' }}
                            />
                        </div>

                        {/* Category & Market */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>
                                    {rtl ? 'التصنيف' : 'Category'}
                                </label>
                                <select
                                    value={category}
                                    onChange={e => setCategory(e.target.value)}
                                    style={{ width: '100%', background: '#0b0f19', border: '1px solid #334155', borderRadius: 8, padding: '10px 12px', color: '#fff', fontSize: 13 }}
                                >
                                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>
                                    {rtl ? 'السوق المستهدف' : 'Target Market'}
                                </label>
                                <select
                                    value={targetMarket}
                                    onChange={e => setTargetMarket(e.target.value)}
                                    style={{ width: '100%', background: '#0b0f19', border: '1px solid #334155', borderRadius: 8, padding: '10px 12px', color: '#fff', fontSize: 13 }}
                                >
                                    {markets.map(m => <option key={m.code} value={m.code}>{m.flag} {m.name} ({m.code})</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Audience Type (Critical Requirement) */}
                        <div style={{ marginBottom: 24, background: '#0b0f19', border: '1px solid #1e293b', borderRadius: 10, padding: 14 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#d4af37', marginBottom: 8 }}>
                                {rtl ? 'نوع الجمهور المستهدف بالبث (Audience Type)' : 'Content Audience Visibility'}
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                                {[
                                    { id: 'RETAIL', label: rtl ? 'تجزئة (عادي)' : 'Retail', desc: rtl ? 'مرئي لكافة العملاء' : 'Visible to all shoppers' },
                                    { id: 'WHOLESALE', label: rtl ? 'جملة فقط' : 'Wholesale', desc: rtl ? 'للتجار المعتمدين فقط' : 'Verified buyers only' },
                                    { id: 'BOTH', label: rtl ? 'تجزئة + جملة' : 'Both (All)', desc: rtl ? 'متاح للجميع' : 'Open to both tiers' }
                                ].map(item => (
                                    <div
                                        key={item.id}
                                        onClick={() => setAudience(item.id)}
                                        style={{
                                            background: audience === item.id ? 'rgba(254,44,85,0.12)' : '#111827',
                                            border: `1.5px solid ${audience === item.id ? '#FE2C55' : '#334155'}`,
                                            borderRadius: 8,
                                            padding: '10px 12px',
                                            cursor: 'pointer',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <strong style={{ fontSize: 13, color: '#f8fafc', display: 'block', marginBottom: 2 }}>{item.label}</strong>
                                        <span style={{ fontSize: 10, color: '#94a3b8' }}>{item.desc}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
                            <button
                                onClick={() => { setShowGoLiveModal(false); stopCamera(); }}
                                style={{ background: '#1e293b', color: '#94a3b8', border: 'none', padding: '10px 18px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                            >
                                {rtl ? 'إلغاء' : 'Cancel'}
                            </button>
                            <button
                                onClick={handleStartLive}
                                style={{ background: 'linear-gradient(135deg, #ef4444, #b91c1c)', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                            >
                                <Radio size={16} />
                                <span>{rtl ? 'بدء البث المباشر الآن' : 'Start Live Broadcast'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Mute User Duration Choices */}
            {showMuteModal && selectedModerationUser && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: '#111827', border: '1px solid #334155', borderRadius: 14, width: '100%', maxWidth: 400, padding: 22, color: '#fff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                            <VolumeX size={20} color="#f59e0b" />
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
                                {rtl ? `كتم المستخدم: ${selectedModerationUser}` : `Mute User: ${selectedModerationUser}`}
                            </h3>
                        </div>
                        <p style={{ margin: '0 0 16px 0', fontSize: 12, color: '#94a3b8' }}>
                            {rtl ? 'المستخدم المكتوم يستمر في مشاهدة البث ولكنه لا يستطيع إرسال رسائل أو أسئلة في المحادثة.' : 'Muted user can continue watching the live stream but cannot send chat messages.'}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                            <button
                                onClick={() => handleMuteUser(5)}
                                style={{ background: '#0b0f19', border: '1px solid #334155', color: '#cbd5e1', padding: '10px 14px', borderRadius: 8, textAlign: 'left', cursor: 'pointer', fontSize: 13 }}
                            >
                                {rtl ? 'كتم لمدة 5 دقائق' : 'Mute for 5 Minutes'}
                            </button>
                            <button
                                onClick={() => handleMuteUser(30)}
                                style={{ background: '#0b0f19', border: '1px solid #334155', color: '#cbd5e1', padding: '10px 14px', borderRadius: 8, textAlign: 'left', cursor: 'pointer', fontSize: 13 }}
                            >
                                {rtl ? 'كتم لمدة 30 دقيقة' : 'Mute for 30 Minutes'}
                            </button>
                            <button
                                onClick={() => handleMuteUser(-1)}
                                style={{ background: '#0b0f19', border: '1px solid #334155', color: '#cbd5e1', padding: '10px 14px', borderRadius: 8, textAlign: 'left', cursor: 'pointer', fontSize: 13 }}
                            >
                                {rtl ? 'كتم حتى نهاية البث المباشر' : 'Mute Until Live Ends'}
                            </button>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowMuteModal(false)}
                                style={{ background: '#1e293b', border: 'none', color: '#94a3b8', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                            >
                                {rtl ? 'إلغاء' : 'Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Post-Live Analytics Report */}
            {showAnalyticsModal && postLiveMetrics && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: '#111827', border: '1px solid rgba(254,44,85,0.4)', borderRadius: 20, width: '100%', maxWidth: 580, padding: 32, boxShadow: '0 25px 50px rgba(0,0,0,0.8)' }}>
                        <div style={{ textAlign: 'center', marginBottom: 24 }}>
                            <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(254,44,85,0.12)', border: '1px solid #FE2C55', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                <Award size={32} color="#FE2C55" />
                            </div>
                            <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 6px 0', color: '#f8fafc' }}>
                                {rtl ? 'تقرير نتائج البث المباشر' : 'Live Broadcast Performance Summary'}
                            </h2>
                            <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>
                                {rtl ? `مدة البث: ${formatDuration(postLiveMetrics.durationSeconds)} • السوق: ${targetMarket}` : `Duration: ${formatDuration(postLiveMetrics.durationSeconds)} • Market: ${targetMarket}`}
                            </p>
                        </div>

                        {/* Metric Tiles */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
                            <div style={{ background: '#0b0f19', border: '1px solid #1e293b', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{rtl ? 'ذروة المشاهدين' : 'Peak Viewers'}</div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#38bdf8' }}>{postLiveMetrics.peakViewers}</div>
                            </div>
                            <div style={{ background: '#0b0f19', border: '1px solid #1e293b', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{rtl ? 'إجمالي المشاهدين' : 'Total Viewers'}</div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#f8fafc' }}>{postLiveMetrics.totalUniqueViewers}</div>
                            </div>
                            <div style={{ background: '#0b0f19', border: '1px solid #1e293b', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{rtl ? 'نقرات المنتجات' : 'Product Clicks'}</div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#FE2C55' }}>{postLiveMetrics.productClicks}</div>
                            </div>
                            <div style={{ background: '#0b0f19', border: '1px solid #1e293b', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{rtl ? 'إضافة للسلة' : 'Add to Bags'}</div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#10b981' }}>{postLiveMetrics.addToBags}</div>
                            </div>
                            <div style={{ background: '#0b0f19', border: '1px solid #1e293b', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{rtl ? 'الطلبات المنفذة' : 'Orders'}</div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#6366f1' }}>{postLiveMetrics.ordersReceived}</div>
                            </div>
                            <div style={{ background: '#0b0f19', border: '1px solid #1e293b', borderRadius: 10, padding: 14, textAlign: 'center' }}>
                                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{rtl ? 'متابعون جدد' : 'Followers +'}</div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#ec4899' }}>+{postLiveMetrics.followersGained}</div>
                            </div>
                        </div>

                        <div style={{ textAlign: 'center' }}>
                            <button
                                onClick={() => setShowAnalyticsModal(false)}
                                style={{ background: '#FE2C55', color: '#ffffff', border: 'none', padding: '12px 34px', borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 14px rgba(254,44,85,0.35)' }}
                            >
                                {rtl ? 'العودة للوحة التحكم' : 'Return to Dashboard'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
